import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { DateField } from '../ui/DateField'

/**
 * Datoriile către furnizori, luate din ExpertAccounts.
 *
 * Ecranul de dinainte le calcula din facturile SPV minus tranzacțiile bancare potrivite
 * una-la-una. Potrivirea cerea suma exactă a întregii facturi într-o fereastră de 60 de zile,
 * și a dat **zero** potriviri din 2.234 de tranzacții — pentru că facturile de furnizor se
 * plătesc în tranșe și târziu. Soldul nu se mișca niciodată.
 *
 * Aici soldul vine de unde e ținut: din contabilitate. Același principiu ca la Tabloul de bord
 * vânzări, doar că pe partea de plăți.
 */

interface Factura {
  docNo: string | null
  furnizor: string
  cif: string | null
  dataDoc: string | null
  scadenta: string | null
  total: number
  restant: number
  zileIntarziere: number | null
  faraTermen: boolean
  /** Plătit din extrase, alocat FIFO — vezi `alocaPlatiPeFacturi`. */
  platit?: number
  restDupaPlati?: number
}

interface Rand {
  furnizor: string
  cif: string | null
  soldAncora: number
  facturiDupaAncora: number
  platiDupaAncora: number
  soldCurent: number
}

interface Reconciliere {
  ancora: string
  soldAncoraTotal: number
  facturiTotal: number
  platiTotal: number
  soldCurentTotal: number
  furnizori: Rand[]
  platiNeatribuite: number
  sumaNeatribuita: number
  facturi: Factura[]
  totalRestDupaPlati: number
  inValuta?: { moneda: string; count: number; faraCurs?: number }[]
}

interface Datorii {
  facturi: Factura[]
  totalRestant: number
  totalIntarziat: number
  totalFaraTermen: number
  laData: string | null
}

function lei(n: number): string {
  return n.toLocaleString('ro-RO', { maximumFractionDigits: 0 }) + ' lei'
}

function zi(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`
}

/**
 * Furnizor extern: cod fiscal cu prefix de țară altul decat RO („LU20260743" — Amazon EU).
 * Fără cod fiscal nu putem spune, deci rămâne intern. Aceeași regulă ca pe server
 * (`facturiRestante/starePeEa.ts`).
 */
function eExtern(cif: string | null): boolean {
  const c = (cif ?? '').trim().toUpperCase().replace(/\s+/g, '')
  return /^[A-Z]{2}/.test(c) && !c.startsWith('RO')
}

type Origine = 'toate' | 'interne' | 'externe'

export function DatoriiFurnizori() {
  const [laData, setLaData] = useState('')
  /** Interne / externe (Alexandru, 14.10.2026). */
  const [origine, setOrigine] = useState<Origine>('toate')
  const [cauta, setCauta] = useState('')
  const [doarIntarziate, setDoarIntarziate] = useState(false)

  /**
   * Reconcilierea, aceeași formulă ca la clienți.
   *
   * Soldul contabil singur nu e răspunsul: e corect până la data la care s-au înregistrat
   * extrasele în EA, și mai mare decât realitatea după ea.
   */
  const recon = useQuery({
    queryKey: ['furnizori-recon'],
    queryFn: async () =>
      (await api.get<Reconciliere>('/facturi-restante/furnizori/reconciliere', { timeout: 90_000 }))
        .data,
    retry: false,
  })

  const date = useQuery({
    queryKey: ['furnizori-ea', laData],
    queryFn: async () =>
      (
        await api.get<Datorii>('/facturi-restante/furnizori', {
          params: laData ? { laData } : {},
          // Apelul merge live la EA și poate dura; pragul obișnuit de 15s e prea scurt.
          timeout: 60_000,
        })
      ).data,
  })

  const termen = cauta.trim().toLowerCase()
  // Aceeasi cautare ca la facturi, aplicata peste randurile reconciliate.
  const furnizoriGasiti = (recon.data?.furnizori ?? []).filter(
    (f) =>
      termen &&
      (f.furnizor.toLowerCase().includes(termen) || (f.cif ?? '').toLowerCase().includes(termen)),
  )
  /*
    Facturile vin din RECONCILIERE cand exista: acolo plățile sunt deja alocate pe documente,
    deci suma coloanei „rest" dă exact soldul reconciliat. Lista simplă din EA rămâne rezerva —
    și singura sursă când se cere soldul la o dată din trecut.
  */
  const sursaFacturi = laData
    ? (date.data?.facturi ?? [])
    : (recon.data?.facturi ?? date.data?.facturi ?? [])

  const dinOrigine = sursaFacturi.filter(
    (f) => origine === 'toate' || (origine === 'externe') === eExtern(f.cif),
  )
  // Cardurile de sus, pe originea aleasă.
  const totalRestant = dinOrigine.reduce((s, f) => s + f.restant, 0)
  const totalIntarziat = dinOrigine
    .filter((f) => !f.faraTermen && (f.zileIntarziere ?? 0) > 0)
    .reduce((s, f) => s + f.restant, 0)
  const totalFaraTermen = dinOrigine.filter((f) => f.faraTermen).reduce((s, f) => s + f.restant, 0)

  const facturi = dinOrigine.filter((f) => {
    if (doarIntarziate && (f.faraTermen || (f.zileIntarziere ?? 0) <= 0)) return false
    // O factură stinsă complet din extrase nu mai e o datorie; n-are ce căuta în listă.
    if ((f.restDupaPlati ?? f.restant) === 0) return false
    if (!termen) return true
    return (
      f.furnizor.toLowerCase().includes(termen) ||
      (f.docNo ?? '').toLowerCase().includes(termen) ||
      (f.cif ?? '').toLowerCase().includes(termen)
    )
  })

  return (
    <div className="space-y-3">
      {recon.data && (
        <Card>
          <div className="p-4">
            <p className="text-sm font-medium text-b4l-text">Sold reconciliat (azi)</p>
            <dl className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-b4l-text-muted">Sold la {zi(recon.data.ancora)}</dt>
                <dd>{lei(recon.data.soldAncoraTotal)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-b4l-text-muted">+ Facturi primite după ancoră</dt>
                <dd>{lei(recon.data.facturiTotal)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-b4l-text-muted">− Plăți din extrase</dt>
                <dd className="text-b4l-green-dark">−{lei(recon.data.platiTotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-b4l-border pt-1 font-medium">
                <dt>= Sold de plată</dt>
                <dd>{lei(recon.data.soldCurentTotal)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-b4l-text-muted">
              Soldul contabil e complet doar până la ancoră. După ea, plățile făcute din bancă dar
              neînregistrate încă în EA se scad aici.
            </p>
            {/*
              Plățile neatribuite se spun, nu se ascund. Un sold reconciliat calculat peste plăți
              pierdute arată mai mic decât realitatea — mai rău decât să n-avem reconciliere.
              Cauza obișnuită: banca trunchiază numele, iar în extras apar și CIF-uri care nu
              sunt CIF-uri.
            */}
            {recon.data.platiNeatribuite > 0 && (
              <p className="mt-2 rounded-lg bg-b4l-amber-light p-2 text-xs text-b4l-amber-dark">
                <strong>{recon.data.platiNeatribuite} plăți</strong> (
                {lei(recon.data.sumaNeatribuita)}) nu au putut fi puse pe niciun furnizor — nume
                trunchiat de bancă sau partener care nu e furnizor. Cu atât e soldul de mai sus mai
                mare decât realitatea.
              </p>
            )}
            {!!recon.data.inValuta?.length && (
              <p className="mt-2 rounded-lg bg-b4l-amber-light p-2 text-xs text-b4l-amber-dark">
                Conține valută:{' '}
                {recon.data.inValuta
                  .map(
                    (v) =>
                      `${v.count} documente în ${v.moneda}` +
                      (v.faraCurs ? ` (${v.faraCurs} fără curs BNR, neincluse)` : ''),
                  )
                  .join(', ')}{' '}
                — incluse în lei, la cursul documentului.
              </p>
            )}
          </div>
        </Card>
      )}

      {recon.isError && (
        <Card>
          <p className="p-3 text-sm text-b4l-amber-dark">
            Reconcilierea are nevoie de ancoră — se pune din Tabloul de bord vânzări și e aceeași
            pentru clienți și furnizori. Mai jos e soldul contabil, neajustat.
          </p>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-b4l-text-dim">De plătit</div>
            <div className="mt-1 text-2xl font-semibold text-b4l-text">{lei(totalRestant)}</div>
            <div className="mt-0.5 text-xs text-b4l-text-muted">{dinOrigine.length} facturi</div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-b4l-text-dim">
              Depășit scadența
            </div>
            <div className="mt-1 text-2xl font-semibold text-b4l-red">{lei(totalIntarziat)}</div>
            <div className="mt-0.5 text-xs text-b4l-text-muted">
              Doar facturile cu termen de plată real, trecut
            </div>
          </div>
        </Card>
        {/*
          Până acum intrau aici și facturile fără termen de plată, care apar „întârziate" a doua
          zi după emitere. Ieșea 99,5% din total — o cifră de care nu se mai putea speria nimeni
          util. Sunt 6 din 10 facturi primite: în e-factură, scadența e chiar ziua emiterii.
        */}
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-b4l-text-dim">
              Fără termen de plată
            </div>
            <div className="mt-1 text-2xl font-semibold text-b4l-text-muted">
              {lei(totalFaraTermen)}
            </div>
            <div className="mt-0.5 text-xs text-b4l-text-muted">
              Scadența = data emiterii. Se plătesc după înțelegerea cu furnizorul.
            </div>
          </div>
        </Card>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <DateField label="Sold la data" value={laData} onChange={setLaData} hint="Gol = azi" />
        </div>
        <div className="flex gap-1 rounded border border-b4l-border bg-b4l-bg-soft p-0.5 text-sm">
          {(['toate', 'interne', 'externe'] as const).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => setOrigine(o)}
              title={
                o === 'externe'
                  ? 'Furnizori cu cod fiscal din altă țară (Amazon, Google, Meta…)'
                  : o === 'interne'
                    ? 'Furnizori din România'
                    : undefined
              }
              className={`rounded px-3 py-1 transition ${
                origine === o ? 'bg-white shadow-sm' : 'text-b4l-text-muted hover:bg-white/60'
              }`}
            >
              {o === 'toate' ? 'Toate' : o === 'interne' ? 'Interne' : 'Externe'}
            </button>
          ))}
        </div>
        <input
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
          placeholder="Furnizor, număr document sau CIF"
          className="min-w-[240px] flex-1 rounded-lg border border-b4l-border px-3 py-2 text-sm"
        />
        <label className="flex items-center gap-2 pb-2 text-sm text-b4l-text-muted">
          <input
            type="checkbox"
            checked={doarIntarziate}
            onChange={(e) => setDoarIntarziate(e.target.checked)}
          />
          Doar cele depășite
        </label>
      </div>

      {/*
        Situația reconciliată PE FURNIZORUL căutat.
        
        Calculam soldul reconciliat pentru fiecare furnizor, dar il aratam doar ca total. Cine
        cauta „d p net" vedea doar tabelul brut din contabilitate, cu plățile nescăzute, si
        concluziona ca reconcilierea n-a functionat. Cifra exista, doar ca n-o vedea nimeni.
      */}
      {termen && furnizoriGasiti.length > 0 && (
        <Card>
          <div className="p-4">
            <p className="text-sm font-medium text-b4l-text">
              Reconciliat pentru{' '}
              {furnizoriGasiti.length === 1 ? 'furnizorul găsit' : 'furnizorii găsiți'}
            </p>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-b4l-text-dim">
                  <tr>
                    <th className="px-2 py-1">Furnizor</th>
                    <th className="px-2 py-1 text-right">Sold la ancoră</th>
                    <th className="px-2 py-1 text-right">+ Facturi</th>
                    <th className="px-2 py-1 text-right">− Plăți</th>
                    <th className="px-2 py-1 text-right">= Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {furnizoriGasiti.map((f) => (
                    <tr key={f.cif ?? f.furnizor} className="border-t border-b4l-border">
                      <td className="px-2 py-1.5">
                        {f.furnizor}
                        {f.cif && <span className="ml-1 text-xs text-b4l-text-dim">{f.cif}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-b4l-text-muted">
                        {lei(f.soldAncora)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-b4l-text-muted">
                        {lei(f.facturiDupaAncora)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-b4l-green-dark">
                        {f.platiDupaAncora > 0 ? `−${lei(f.platiDupaAncora)}` : lei(0)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                        {lei(f.soldCurent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-b4l-text-muted">
              Plățile vin din extrasele bancare, potrivite după CIF sau după nume. Tabelul de mai
              jos e lista brută din ExpertAccounts, unde plățile apar doar după ce contabilitatea le
              înregistrează pe documente.
            </p>
          </div>
        </Card>
      )}

      <Card>
        {date.isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">
            Se întreabă ExpertAccounts… poate dura câteva secunde.
          </p>
        ) : date.isError ? (
          <p className="p-4 text-sm text-b4l-red">
            Nu s-a putut citi soldul din ExpertAccounts. Verifică tokenii pentru solduri în Setări →
            Integrări → ExpertAccounts.
          </p>
        ) : facturi.length === 0 ? (
          <p className="p-4 text-sm text-b4l-text-muted">Nicio factură de plătit.</p>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-b4l-text-dim [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-b4l-surface [&_th]:shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)]">
                <tr>
                  <th className="px-3 py-2">Furnizor</th>
                  <th className="px-3 py-2">Document</th>
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2">Scadență</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Plătit din extrase</th>
                  <th className="px-3 py-2 text-right">Rest de plată</th>
                </tr>
              </thead>
              <tbody>
                {facturi.map((f, i) => {
                  const intarziat = !f.faraTermen && (f.zileIntarziere ?? 0) > 0
                  // Restul de după plăți când îl avem; altfel cel din contabilitate.
                  const rest = f.restDupaPlati ?? f.restant
                  return (
                    <tr key={`${f.docNo}-${i}`} className="border-t border-b4l-border">
                      <td className="px-3 py-2">
                        {f.furnizor}
                        {f.cif && <span className="ml-1 text-xs text-b4l-text-dim">{f.cif}</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{f.docNo ?? '—'}</td>
                      <td className="px-3 py-2 text-b4l-text-muted">{zi(f.dataDoc)}</td>
                      <td className="px-3 py-2">
                        <span className={intarziat ? 'text-b4l-red' : 'text-b4l-text-muted'}>
                          {f.faraTermen ? 'fără termen' : zi(f.scadenta)}
                          {f.zileIntarziere !== null && intarziat && ` · ${f.zileIntarziere}z`}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-b4l-text-muted">{lei(f.total)}</td>
                      <td className="px-3 py-2 text-right text-b4l-green-dark">
                        {f.platit ? `−${lei(f.platit)}` : '—'}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-medium ${
                          rest < 0 ? 'text-b4l-green-dark' : 'text-b4l-text'
                        }`}
                      >
                        {lei(rest)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-b4l-text-muted">
        „Plătit din extrase” sunt banii ieșiți din bancă, alocați pe documente de la cel mai vechi
        către cel mai nou — la fel ca încasările din Tabloul de bord vânzări. În ExpertAccounts ei
        apar abia după ce contabilitatea îi înregistrează pe factură, deci cele două liste pot arăta
        diferit până atunci. Documentele cu rest negativ sunt storno sau note de credit.
      </p>
    </div>
  )
}
